/**
 * Presence state — self-presence transitions + others' presence map.
 *
 * Self-state machine per CLIENT_DESIGN §13.1:
 *   Online  — WebSocket connected, shell focused, world channel NOT active
 *   InWorld — WebSocket connected, world channel active
 *   Away    — tab blurred > `away_after_ms` (5 min)
 *   Offline — WebSocket disconnected (derived from wire layer)
 *
 * Others' presence arrives via `EventEnvelope::PresenceUpdate` and lands
 * here keyed by PlayerId; the channel list + DM pane + avatar render
 * read from this map to paint the coloured dot.
 */

import { create } from 'zustand';

import type { PlayerId, Presence } from '../wire';

export interface PresenceState {
  self: Presence;
  /** `since` tick of the self-presence state (bumped on every transition). */
  selfSince: bigint;
  /** Server-sourced presence for every other player the client knows about. */
  others: Map<PlayerId, { presence: Presence; since: bigint }>;

  // ─── Actions ─────────────────────────────────────────────────────
  setSelf: (next: Presence, since: bigint) => void;
  setOther: (player: PlayerId, presence: Presence, since: bigint) => void;
  removeOther: (player: PlayerId) => void;
  reset: () => void;
}

export const usePresence = create<PresenceState>((set) => ({
  self: 'Online',
  selfSince: 0n,
  others: new Map(),

  setSelf: (next, since) =>
    set(() => ({ self: next, selfSince: since })),

  setOther: (player, presence, since) =>
    set((s) => {
      const next = new Map(s.others);
      next.set(player, { presence, since });
      return { others: next };
    }),

  removeOther: (player) =>
    set((s) => {
      if (!s.others.has(player)) return s;
      const next = new Map(s.others);
      next.delete(player);
      return { others: next };
    }),

  reset: () =>
    set(() => ({
      self: 'Online',
      selfSince: 0n,
      others: new Map(),
    })),
}));

/** Color swatch for the presence dot per CLIENT_DESIGN §13.1. */
export function presenceColor(p: Presence): string {
  switch (p) {
    case 'Online':
      return '#57f287';
    case 'InWorld':
      return '#f4a72a';
    case 'Away':
      return '#faa61a';
    case 'Offline':
      return '#747f8d';
  }
}
